// src/pages/Partner/PartnerLayout.jsx

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BookOpen,
  CircleHelp,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Users,
  UsersRound,
  X,
} from "lucide-react";

import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { createPortal } from "react-dom";

import { supabase } from "../../supabaseClient";

import {
  getMyPortalAccess,
} from "../../lib/access/portalAccess";


const BRAND_ORANGE =
  "#FF914D";


/* -------------------------------------------------------------------------- */
/* Layout                                                                     */
/* -------------------------------------------------------------------------- */

export default function PartnerLayout() {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  // The desktop sidebar is rendered in document.body. Measure the actual
  // bottom of the header so browser zoom, CSS overrides, and responsive
  // breakpoints cannot leave a strip of workspace above the sidebar.
  const headerRef = useRef(null);
  const [headerBottom, setHeaderBottom] = useState(92);


  const [
    access,
    setAccess,
  ] = useState(null);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    error,
    setError,
  ] = useState("");


  const [
    mobileMenuOpen,
    setMobileMenuOpen,
  ] = useState(false);


  /* ------------------------------------------------------------------------ */
  /* Access                                                                   */
  /* ------------------------------------------------------------------------ */

  const loadAccess =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const nextAccess =
            await getMyPortalAccess();

          setAccess(
            nextAccess
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner layout access load failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The partner organisation could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      []
    );


  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return undefined;

    const measureHeader = () => {
      const nextBottom = Math.max(
        0,
        Math.round(header.getBoundingClientRect().bottom)
      );
      setHeaderBottom((current) =>
        current === nextBottom ? current : nextBottom
      );
    };

    measureHeader();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measureHeader)
        : null;
    observer?.observe(header);

    window.addEventListener("resize", measureHeader);
    window.addEventListener("scroll", measureHeader, { passive: true });

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measureHeader);
      window.removeEventListener("scroll", measureHeader);
    };
  }, [loading, error, access?.partner_id]);


  /* ------------------------------------------------------------------------ */
  /* Drawer behaviour                                                         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setMobileMenuOpen(
      false
    );
  }, [location.pathname]);


  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        "Escape"
      ) {
        setMobileMenuOpen(
          false
        );
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [mobileMenuOpen]);


  /* ------------------------------------------------------------------------ */
  /* Partner name updates                                                     */
  /* ------------------------------------------------------------------------ */

  const handlePartnerUpdated =
    useCallback(
      (updatedPartner) => {
        setAccess(
          (current) => {
            if (!current) {
              return current;
            }

            return {
              ...current,

              partner_name:
                updatedPartner?.name ||
                current.partner_name,
            };
          }
        );
      },
      []
    );


  /* ------------------------------------------------------------------------ */
  /* Logout                                                                   */
  /* ------------------------------------------------------------------------ */

  async function handleLogout() {
    await supabase.auth
      .signOut();

    navigate(
      "/partner/login",
      {
        replace: true,
      }
    );
  }


  /* ------------------------------------------------------------------------ */
  /* Loading                                                                  */
  /* ------------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F4F4] px-6 font-sans">
        <div className="text-center">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#FF914D] text-sm font-black text-[#FF914D]">
            G
          </div>

          <p className="mt-4 text-sm font-semibold text-[#777777]">
            Loading Partner Portal...
          </p>
        </div>
      </div>
    );
  }


  /* ------------------------------------------------------------------------ */
  /* Error                                                                    */
  /* ------------------------------------------------------------------------ */

  if (
    error ||
    !access?.partner_id
  ) {
    return (
      <div className="min-h-screen bg-[#F4F4F4] px-6 py-10 font-sans">
        <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white p-6">
          <h1 className="text-xl font-black text-black">
            Partner Portal unavailable
          </h1>

          <p className="mt-3 text-sm text-red-700">
            {error ||
              "No active partner organisation was found."}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={
                loadAccess
              }
              className="rounded-lg border border-[#D5D5D5] bg-white px-5 py-3 text-sm font-black text-black transition hover:border-[#FF914D]"
            >
              Try again
            </button>

            <button
              type="button"
              onClick={
                handleLogout
              }
              className="rounded-lg bg-black px-5 py-3 text-sm font-black text-white transition hover:bg-[#252525]"
            >
              Return to partner login
            </button>
          </div>
        </div>
      </div>
    );
  }


  const role =
    access.partner_role ||
    "";


  const canManageAccount =
    role !==
    "facilitator";


  const canManageTeam =
    [
      "owner",
      "admin",
    ].includes(role);


  const helpRoute =
  "/partner/support";


  const navigationItems = [
    {
      to: "/partner",
      label: "Overview",
      icon: LayoutDashboard,
      end: true,
    },

    {
      to: "/partner/cohorts",
      label: "Cohorts",
      icon: UsersRound,
    },

    {
      to: "/partner/curriculum",
      label: "Curriculum",
      icon: BookOpen,
    },

    ...(canManageAccount
      ? [
          {
            to: "/partner/settings",
            label: "Account",
            icon: Settings,
          },
        ]
      : []),

    ...(canManageTeam
      ? [
          {
            to: "/partner/team",
            label: "Team",
            icon: Users,
          },
        ]
      : []),

    {
      to: helpRoute,
      label: "Help",
      icon: CircleHelp,
    },
  ];


  return (
    <div className="min-h-screen bg-[#F4F4F4] font-sans text-black">

      {/* ================================================================ */}
      {/* GLOBAL HEADER                                                    */}
      {/* ================================================================ */}

      <header ref={headerRef} className="sticky top-0 z-50 h-[78px] border-b border-[#D7D7D7] bg-white lg:h-[92px]">
        <div className="flex h-full items-center justify-between px-4 sm:px-6 lg:px-[86px]">

          <NavLink
            to="/partner"
            className="flex items-center gap-2.5"
            aria-label="Gwin for Partners home"
          >
            <GwinBrand />
          </NavLink>


          <button
            type="button"
            onClick={() =>
              setMobileMenuOpen(
                true
              )
            }
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#D5D5D5] bg-white text-black lg:hidden"
            aria-label="Open partner navigation"
            aria-expanded={
              mobileMenuOpen
            }
          >
            <Menu className="h-5 w-5" />
          </button>

        </div>
      </header>


      {/* ================================================================ */}
      {/* DESKTOP SIDEBAR (PORTALED TO DOCUMENT BODY)                      */}
      {/* ================================================================ */}

      {typeof document !== "undefined" &&
        createPortal(
          <aside
            data-gwin-partner-sidebar="desktop"
            className="hidden bg-black lg:flex lg:flex-col lg:overflow-hidden"
            style={{
              position: "fixed",
              left: 0,
              top: 0,
              bottom: 0,
              width: 250,
              // Extend black behind the white header, and start the
              // sidebar content immediately below its measured bottom.
              paddingTop: headerBottom,
              boxSizing: "border-box",
              zIndex: 40,
            }}
          >
            <SidebarContent
              access={access}
              navigationItems={navigationItems}
              onLogout={handleLogout}
            />
          </aside>,
          document.body
        )}

      {/* ================================================================ */}
      {/* BODY                                                             */}
      {/* ================================================================ */}

      <div className="min-h-[calc(100dvh-78px)] lg:min-h-[calc(100dvh-92px)] lg:pl-[250px]">
        <main className="mx-auto w-full max-w-[1450px] px-5 py-8 sm:px-8 lg:px-8 lg:py-9 xl:px-10">
          <Outlet
            context={{
              access,
              onPartnerUpdated: handlePartnerUpdated,
            }}
          />
        </main>
      </div>


      {/* ================================================================ */}
      {/* MOBILE NAVIGATION                                               */}
      {/* ================================================================ */}

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">

          <button
            type="button"
            className="absolute inset-0 bg-black/45"
            aria-label="Close partner navigation"
            onClick={() =>
              setMobileMenuOpen(
                false
              )
            }
          />


          <aside className="absolute inset-y-0 left-0 flex w-[min(315px,88vw)] flex-col bg-black shadow-2xl">

            <div className="flex items-center justify-between border-b border-white/10 px-5 py-5">
              <GwinBrand
                inverted
              />

              <button
                type="button"
                onClick={() =>
                  setMobileMenuOpen(
                    false
                  )
                }
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white"
                aria-label="Close partner navigation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>


            <SidebarContent
              access={access}
              navigationItems={
                navigationItems
              }
              onLogout={
                handleLogout
              }
              mobile
            />

          </aside>
        </div>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Brand                                                                      */
/* -------------------------------------------------------------------------- */

function GwinBrand({
  inverted = false,
}) {
  return (
    <div className="flex items-center">
      <img
        src="https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png"
        alt="Gwin"
        className="mr-18 -mt-1 h-24 w-26 flex-none object-contain sm:h-26 sm:w-28"
      />

      <span
        className={[
          "text-[19px] font-medium tracking-[-0.035em]",
          inverted
            ? "text-white/90"
            : "text-[#262626]",
        ].join(" ")}
      >
        for Partners
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sidebar                                                                    */
/* -------------------------------------------------------------------------- */

function SidebarContent({
  access,
  navigationItems,
  onLogout,
  mobile = false,
}) {
  const items =
    useMemo(
      () =>
        navigationItems,
      [navigationItems]
    );


  return (
    <>
      {/* Organisation */}

      <div className="px-6 pb-5 pt-10">
        <p className="truncate text-[15px] font-black text-white">
          {access.partner_name ||
            "Organisation name"}
        </p>
      </div>


      {/* Navigation */}

      <nav
  aria-label="Partner navigation"
  className="
    flex-1
    overflow-y-auto
    px-5
    pb-5

    [scrollbar-width:thin]
    [scrollbar-color:#FF914D_#000000]

    [&::-webkit-scrollbar]:w-[6px]
    [&::-webkit-scrollbar-track]:bg-black
    [&::-webkit-scrollbar-thumb]:rounded-full
    [&::-webkit-scrollbar-thumb]:bg-[#FF914D]
  "
>
        <div className="space-y-4">
          {items.map(
            (item) => {
              const Icon =
                item.icon;

              return (
                <NavLink
                  key={
                    item.to
                  }
                  to={
                    item.to
                  }
                  end={
                    item.end
                  }
                  className={({
                    isActive,
                  }) =>
                    [
                      "group flex min-h-[44px] items-center gap-3 rounded-[10px] px-3.5 py-2.5 text-sm font-medium transition",

                      isActive
                        ? "bg-[#262626] text-white"
                        : "text-[#E9E9E9] hover:bg-[#171717] hover:text-white",
                    ].join(" ")
                  }
                >
                  {({
                    isActive,
                  }) => (
                    <>
                      <Icon
                        className={[
                          "h-[20px] w-[20px] flex-none stroke-[1.55] transition",
                          isActive
                            ? "text-[#FF914D]"
                            : "text-[#FF914D]",
                        ].join(" ")}
                      />

                      <span className="truncate">
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            }
          )}
        </div>
      </nav>


      {/* Logout */}

      <div className="border-t border-white/10 px-5 py-5">
        <button
          type="button"
          onClick={
            onLogout
          }
          className="flex w-full items-center rounded-[10px] px-3.5 py-3 text-sm font-medium text-[#DADADA] transition hover:bg-[#171717] hover:text-white"
        >
          <LogOut className="mr-3 h-[19px] w-[19px] text-[#FF914D]" />

          Log out
        </button>

        {!mobile && (
          <p className="mt-3 truncate px-3.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">
            {access.partner_role ||
              "partner"}
          </p>
        )}
      </div>
    </>
  );
}